import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { logJourney } from "@/lib/journey";
import { MarketplaceFooterStrip } from "../components/MarketplaceFooterStrip";
import "./AdvisorProfile.css";

const API = process.env.REACT_APP_BACKEND_URL;
const initials = (s) => (s || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "AD";

const CHAPTERS_BY_AREA = {
  ca_auditor: ["Restated Financials", "Capitalisation", "Indebtedness", "MD&A"],
  peer_review_auditor: ["Restated Financials", "Auditor's Report", "Financial Statements"],
  cfo: ["Restated Financials", "MD&A", "Objects of the Issue", "Capitalisation"],
  tax_advisor: ["Statement of Tax Benefits", "Restated Financials"],
  company_secretary: ["Our Management", "Capital Structure", "Regulatory Approvals"],
  legal_advisor: ["Legal & Regulatory", "Outstanding Litigation", "Material Contracts", "Risk Factors"],
  independent_director: ["Our Management", "Corporate Governance"],
  valuation_expert: ["Basis of Issue Price", "Financial Indebtedness"],
  merchant_banker: ["Issue Structure", "Basis of Issue Price", "Objects of the Issue", "Risk Factors"],
};

const emptyReg = () => ({ authority: "", reg_type: "", reg_number: "" });
const emptyTrack = () => ({ project_name: "", exchange: "", issue_size: "", year: "", details: "" });
const emptyFee = () => ({ label: "", amount: "" });

export default function AdvisorProfile() {
  const { expertId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/advisor/${expertId}/profile`, { credentials: "include" });
      if (!r.ok) { setNotFound(true); return; }
      setP(await r.json());
    } catch { setNotFound(true); }
  }, [expertId]);

  useEffect(() => { load(); logJourney("Advisor Marketplace", "Viewed", `Opened advisor profile ${expertId}`); }, [load, expertId]);

  if (notFound) return (
    <div className="pf" data-testid="advisor-profile-notfound">
      <div className="top"><div className="brand" data-testid="ap-brand" onClick={() => navigate("/advisor-marketplace")}>IPO<span>Labs</span></div><div className="back" onClick={() => navigate("/advisor-marketplace")}>← Back to results</div></div>
      <div style={{ padding: 40, textAlign: "center", color: "#8a8a94" }}>Advisor not found. <span style={{ color: "#185FA5", cursor: "pointer" }} onClick={() => navigate("/advisor-marketplace")}>Back to marketplace →</span></div>
    </div>
  );
  if (!p) return <div className="pf" data-testid="advisor-profile-loading"><div className="top"><div className="brand">IPO<span>Labs</span></div></div><div style={{ padding: 40, color: "#8a8a94" }}>Loading profile…</div></div>;

  const name = p.firm_name || p.full_name;
  const role = p.expertise_labels?.[0] || "IPO Advisor";
  const areaKey = p.expertise_areas?.[0];
  const chapters = CHAPTERS_BY_AREA[areaKey] || ["Risk Factors", "Business Overview", "Our Management"];
  const icaiReg = (p.authority_regs || [])[0];
  const track = p.track_record || [];
  const fees = p.indicative_fees || [];
  const about = p.about || `${name} is a ${p.city}-based ${role.toLowerCase()} available for IPO mandates${p.ipo_experience ? ` with ${p.years_of_experience}+ years across Indian mainboard & SME IPO listings` : ""}. ${p.work_location === "pan_india" ? "Takes up Pan-India mandates via online meetings." : "Prefers in-person engagement within their city."}`;

  return (
    <div className="pf" data-testid="advisor-profile-page">
      <div className="top">
        <div className="brand" data-testid="ap-brand" onClick={() => navigate("/advisor-marketplace")}>IPO<span>Labs</span></div>
        <div className="back" onClick={() => navigate("/advisor-marketplace")} data-testid="ap-back"><i className="ti ti-arrow-left" /> Back to results</div>
        {p.is_owner && <button className="owner-edit" onClick={() => setEditOpen(true)} data-testid="ap-manage-profile" style={{ marginLeft: 12 }}>✎ Manage my profile</button>}
      </div>

      <div className="head">
        <div className="h-av" data-testid="ap-avatar">{p.has_photo ? <img src={`${API}/api/advisor/${p.expert_id}/image/photo`} alt={name} onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentNode.textContent = initials(name); }} /> : initials(name)}</div>
        <div className="h-mid">
          <div className="h-n" data-testid="ap-name">
            {name}
            {p.is_premium && <span className="h-crown"><i className="ti ti-crown" style={{ fontSize: 9 }} /> Premium</span>}
            <button className="pbtn pbtn-msg h-n-msg" onClick={() => setMsgOpen(true)} data-testid="ap-message"><i className="ti ti-message-2" style={{ fontSize: 12 }} /> Message</button>
          </div>
          <div className="h-role">{role} · {p.city}{p.state ? `, ${p.state}` : ""}</div>
          <div className="h-badges">
            <span className="bdg b-sebi">SEBI aligned</span>
            {p.is_verified && <span className="bdg b-ver">✓ Verified</span>}
            {icaiReg?.reg_number && <span className="bdg b-icai">{icaiReg.authority || "ICAI"} · {icaiReg.reg_number}</span>}
            {p.work_location === "pan_india" && <span className="bdg b-pan">Pan-India · online</span>}
          </div>
        </div>
      </div>

      <div className="stats">
        <div className="st"><div className="st-v">{p.ipo_experience ? `${p.years_of_experience}+ yrs` : "New"}</div><div className="st-l">IPO experience</div></div>
        <div className="st"><div className="st-v">{(p.verified_areas || []).length}/{(p.expertise_areas || []).length || 1}</div><div className="st-l">areas verified</div></div>
        <div className="st"><div className="st-v">{p.work_location === "city_only" ? "City only" : "Pan-India"}</div><div className="st-l">Availability</div></div>
        <div className="st"><div className="st-v">{p.city || "—"}</div><div className="st-l">Based in</div></div>
      </div>

      <div className="body">
        <div>
          <div className="sec">
            <div className="sec-t"><i className="ti ti-user" style={{ color: "#185FA5" }} /> About</div>
            <div className="about" data-testid="ap-about">{about}</div>
          </div>
          {(track.length > 0 || p.is_owner) && (
            <div className="sec" data-testid="ap-track-record">
              <div className="sec-t"><i className="ti ti-briefcase" style={{ color: "#185FA5" }} /> Track record — recent IPOs</div>
              {track.length > 0 ? track.map((d, i) => (
                <div className="deal" key={i} data-testid={`ap-track-item-${i}`}>
                  <div className="deal-ic"><i className="ti ti-building-factory" /></div>
                  <div>
                    <div className="deal-n">{d.project_name}</div>
                    <div className="deal-d">{[d.issue_size, d.exchange, d.year, d.details].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              )) : (
                <div className="ap-empty" data-testid="ap-track-empty">
                  You haven&apos;t added any IPO projects yet.
                  <button className="ap-empty-add" onClick={() => setEditOpen(true)} data-testid="ap-track-empty-add">＋ Add your track record</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="scard" data-testid="ap-credentials">
            <div className="scard-t">Verified credentials</div>
            {p.is_verified && <div className="cred"><i className="ti ti-rosette-discount-check" /> Identity verified by IPOLabs</div>}
            {(p.authority_regs || []).length > 0
              ? p.authority_regs.map((r, i) => (<div className="cred" key={i}><i className="ti ti-rosette-discount-check" /> {r.authority || "Authority"} · {r.reg_type || "Reg."} {r.reg_number}</div>))
              : (p.verified_areas || []).map((a, i) => (<div className="cred" key={i}><i className="ti ti-rosette-discount-check" /> {p.expertise_labels?.[p.expertise_areas.indexOf(a)] || a} verified</div>))}
            {(p.authority_regs || []).length === 0 && (p.verified_areas || []).length === 0 && <div className="cred" style={{ color: "#8a8a94" }}><i className="ti ti-clock" style={{ color: "#a86711" }} /> Verification pending</div>}
            <div className="verify-box" style={{ marginTop: 8 }}><b>{p.is_verified ? "Independently verified" : "Self-declared"}</b> — {p.is_verified ? "checked by IPOLabs against ICAI / SEBI registries." : "please complete verification to earn a verified badge."}</div>
          </div>

          <div className="scard" data-testid="ap-contact">
            <div className="scard-t"><i className="ti ti-address-book" /> Firm &amp; contact</div>
            {p.has_firm_logo && <img className="card-thumb" src={`${API}/api/advisor/${p.expert_id}/image/firm_logo`} alt="Firm logo" style={{ maxHeight: 80, objectFit: "contain", background: "#fafbfc" }} />}
            {p.mobile && <div className="cred"><i className="ti ti-phone" style={{ color: "#185FA5" }} /> {p.mobile}</div>}
            {p.email && <div className="cred"><i className="ti ti-mail" style={{ color: "#185FA5" }} /> {p.email}</div>}
            {p.has_visiting_card && <><div style={{ fontSize: 9, color: "#8a8a94", margin: "8px 0 2px" }}>Visiting card</div><img className="card-thumb" src={`${API}/api/advisor/${p.expert_id}/image/visiting_card`} alt="Visiting card" /></>}
            {!p.mobile && !p.email && !p.has_visiting_card && <div className="cred" style={{ color: "#8a8a94" }}>Contact via Message →</div>}
          </div>

          <div className="scard">
            <div className="scard-t">Chapters they&apos;ll own</div>
            <div>{chapters.map((c, i) => (<span className="chapter-chip" key={i}><i className="ti ti-file-text" style={{ fontSize: 10 }} /> {c}</span>))}</div>
          </div>

          {(fees.length > 0 || p.is_owner) && (
            <div className="scard" data-testid="ap-indicative-fees">
              <div className="scard-t">Indicative fees</div>
              {fees.length > 0 ? (
                <>
                  {fees.map((f, i) => (<div className="fee-row" key={i} data-testid={`ap-fee-item-${i}`}><span>{f.label}</span><b>{f.amount}</b></div>))}
                  <div style={{ fontSize: 9, color: "#8a8a94", marginTop: 7 }}>Final fees agreed directly in your engagement letter.</div>
                </>
              ) : (
                <div className="ap-empty" data-testid="ap-fees-empty">
                  No indicative fees listed yet.
                  <button className="ap-empty-add" onClick={() => setEditOpen(true)} data-testid="ap-fees-empty-add">＋ Add indicative fees</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <MarketplaceFooterStrip />

      <MessageDialog open={msgOpen} onOpenChange={setMsgOpen} expertId={p.expert_id} name={name} />
      {p.is_owner && <OwnerEditDialog open={editOpen} onOpenChange={setEditOpen} p={p} onSaved={() => { setEditOpen(false); load(); }} />}
    </div>
  );
}

const MessageDialog = ({ open, onOpenChange, expertId, name }) => {
  const [f, setF] = useState({ name: "", email: "", mobile: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const send = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.message.trim()) { toast.error("Please add your name, email and a short message."); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/matchmaker/experts/${expertId}/enquiry`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: f.name, email: f.email, mobile: f.mobile, message: f.message, subject: `Enquiry for ${name}` }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { toast.success(d.message || `Enquiry sent to ${name}`); onOpenChange(false); setF({ name: "", email: "", mobile: "", message: "" }); }
      else { toast.error(d.detail || "Could not send your enquiry"); }
    } catch { toast.error("Connection error"); }
    setSending(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ap-message-dialog">
        <DialogHeader><DialogTitle>Message {name}</DialogTitle><DialogDescription className="sr-only">Send an enquiry to this advisor. It reaches our team and is emailed to the advisor.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Your name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ap-msg-name" />
            <Input placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="ap-msg-email" />
          </div>
          <Input placeholder="Mobile (optional)" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="ap-msg-mobile" />
          <Textarea rows={5} placeholder="Describe your requirement, timeline and what you need…" value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="ap-msg-body" />
          <p style={{ fontSize: 11, color: "#8a8a94", margin: 0 }}>Your enquiry reaches our team and is emailed to the advisor.</p>
        </div>
        <DialogFooter>
          <button className="pbtn" style={{ background: "#eef0f3", color: "#44444e" }} onClick={() => onOpenChange(false)}>Cancel</button>
          <button className="pbtn pbtn-msg" disabled={sending} onClick={send} data-testid="ap-msg-send">{sending ? "Sending…" : "Send enquiry"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UploadTile = ({ expertId, kind, label, has, onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const pick = () => ref.current?.click();
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("expert_id", expertId);
    try {
      const r = await fetch(`${API}/api/advisor/me/upload/${kind}`, { method: "POST", credentials: "include", body: fd });
      if (r.ok) { toast.success(`${label} uploaded`); setBust(Date.now()); onDone?.(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Upload failed"); }
    } catch { toast.error("Connection error"); }
    setBusy(false);
  };
  const show = has || bust;
  return (
    <div className="pf-upload" onClick={pick} data-testid={`ap-upload-${kind}`}>
      {show ? <img src={`${API}/api/advisor/${expertId}/image/${kind}?t=${bust}`} alt={label} /> : <div className="pf-upload-ph"><i className="ti ti-camera-plus" /></div>}
      <div className="pf-upload-lbl">{busy ? "Uploading…" : label}</div>
      <input ref={ref} type="file" accept="image/*,.pdf" hidden onChange={upload} />
    </div>
  );
};

const OwnerEditDialog = ({ open, onOpenChange, p, onSaved }) => {
  const [firmName, setFirmName] = useState(p.firm_name || "");
  const [mobile, setMobile] = useState(p.mobile || "");
  const [email, setEmail] = useState(p.email || "");
  const [about, setAbout] = useState(p.about || "");
  const [regs, setRegs] = useState((p.authority_regs || []).length ? p.authority_regs.map((r) => ({ ...r })) : [emptyReg()]);
  const [track, setTrack] = useState((p.track_record || []).length ? p.track_record.map((r) => ({ ...r })) : []);
  const [fees, setFees] = useState((p.indicative_fees || []).length ? p.indicative_fees.map((r) => ({ ...r })) : []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/advisor/me/profile-extra`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expert_id: p.expert_id,
          firm_name: firmName, mobile, email, about,
          authority_regs: regs.filter((x) => x.reg_number || x.authority),
          track_record: track.filter((x) => (x.project_name || "").trim()),
          indicative_fees: fees.filter((x) => (x.label || "").trim() && (x.amount || "").trim()),
        }),
      });
      if (r.ok) { toast.success("Profile updated"); onSaved(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Could not save"); }
    } catch { toast.error("Connection error"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="ap-edit-dialog">
        <DialogHeader><DialogTitle>Manage my advisor profile</DialogTitle><DialogDescription className="sr-only">Upload your photo, firm logo and visiting card, and edit your firm details and authority registrations.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Photo · firm logo · visiting card</div>
            <div className="pf-edit-grid">
              <UploadTile expertId={p.expert_id} kind="photo" label="Your photo" has={p.has_photo} />
              <UploadTile expertId={p.expert_id} kind="firm_logo" label="Firm logo" has={p.has_firm_logo} />
              <UploadTile expertId={p.expert_id} kind="visiting_card" label="Visiting card" has={p.has_visiting_card} />
            </div>
          </div>
          <div className="pf-edit-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Firm / display name</label><Input value={firmName} onChange={(e) => setFirmName(e.target.value)} data-testid="ap-edit-firm" placeholder={p.full_name} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600 }}>Mobile</label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="ap-edit-mobile" /></div>
          </div>
          <div><label style={{ fontSize: 11, fontWeight: 600 }}>Email</label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ap-edit-email" /></div>
          <div><label style={{ fontSize: 11, fontWeight: 600 }}>About</label><Textarea rows={3} value={about} onChange={(e) => setAbout(e.target.value)} data-testid="ap-edit-about" /></div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Authority verification (ICAI / ICSI / SEBI / IBBI / MCA)</div>
            {regs.map((r, i) => (
              <div className="pf-reg-row" key={i}>
                <Input placeholder="Authority (e.g. ICAI)" value={r.authority} onChange={(e) => setRegs((s) => s.map((x, j) => j === i ? { ...x, authority: e.target.value } : x))} data-testid={`ap-reg-authority-${i}`} />
                <Input placeholder="Type (e.g. FRN)" value={r.reg_type} onChange={(e) => setRegs((s) => s.map((x, j) => j === i ? { ...x, reg_type: e.target.value } : x))} data-testid={`ap-reg-type-${i}`} />
                <Input placeholder="Number" value={r.reg_number} onChange={(e) => setRegs((s) => s.map((x, j) => j === i ? { ...x, reg_number: e.target.value } : x))} data-testid={`ap-reg-number-${i}`} />
                <button className="owner-edit" style={{ border: "none", color: "#c0392b" }} onClick={() => setRegs((s) => s.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="owner-edit" style={{ borderStyle: "dashed" }} onClick={() => setRegs((s) => [...s, emptyReg()])} data-testid="ap-reg-add">+ Add registration</button>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Track record — recent IPO projects <span style={{ color: "#9BA3AD", fontWeight: 400 }}>· optional</span></div>
            {track.map((t, i) => (
              <div className="pf-track-row" key={i} data-testid={`ap-track-row-${i}`}>
                <Input placeholder="Project / company name" value={t.project_name} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, project_name: e.target.value } : x))} data-testid={`ap-track-name-${i}`} />
                <div className="pf-track-sub">
                  <Input placeholder="Exchange (e.g. BSE SME)" value={t.exchange} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, exchange: e.target.value } : x))} data-testid={`ap-track-exchange-${i}`} />
                  <Input placeholder="Issue size (e.g. ₹28 Cr)" value={t.issue_size} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, issue_size: e.target.value } : x))} data-testid={`ap-track-size-${i}`} />
                  <Input placeholder="Year (e.g. 2025)" value={t.year} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} data-testid={`ap-track-year-${i}`} />
                </div>
                <div className="pf-track-details">
                  <Input placeholder="Short details (e.g. restated financials + audit)" value={t.details} onChange={(e) => setTrack((s) => s.map((x, j) => j === i ? { ...x, details: e.target.value } : x))} data-testid={`ap-track-details-${i}`} />
                  <button className="owner-edit" style={{ border: "none", color: "#c0392b" }} onClick={() => setTrack((s) => s.filter((_, j) => j !== i))} data-testid={`ap-track-remove-${i}`}>✕</button>
                </div>
              </div>
            ))}
            <button className="owner-edit" style={{ borderStyle: "dashed" }} onClick={() => setTrack((s) => [...s, emptyTrack()])} data-testid="ap-track-add">+ Add IPO project</button>
          </div>

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Indicative fees <span style={{ color: "#9BA3AD", fontWeight: 400 }}>· optional</span></div>
            {fees.map((f, i) => (
              <div className="pf-fee-row" key={i} data-testid={`ap-fee-row-${i}`}>
                <Input placeholder="Fee item (e.g. Statutory audit & restatement)" value={f.label} onChange={(e) => setFees((s) => s.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`ap-fee-label-${i}`} />
                <Input placeholder="Amount (e.g. ₹6.5L)" value={f.amount} onChange={(e) => setFees((s) => s.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`ap-fee-amount-${i}`} />
                <button className="owner-edit" style={{ border: "none", color: "#c0392b" }} onClick={() => setFees((s) => s.filter((_, j) => j !== i))} data-testid={`ap-fee-remove-${i}`}>✕</button>
              </div>
            ))}
            <button className="owner-edit" style={{ borderStyle: "dashed" }} onClick={() => setFees((s) => [...s, emptyFee()])} data-testid="ap-fee-add">+ Add fee row</button>
          </div>
        </div>
        <DialogFooter>
          <button className="pbtn" style={{ background: "#eef0f3", color: "#44444e" }} onClick={() => onOpenChange(false)}>Cancel</button>
          <button className="pbtn pbtn-msg" disabled={saving} onClick={save} data-testid="ap-edit-save">{saving ? "Saving…" : "Save profile"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
